import useSimDetails from 'api/sims/useSimDetails';
import useSimDeviceDetails from 'api/sims/useSimDeviceDetails';
import useSimStatuses from 'api/sims/useSimStatuses';
import useUserInfo from 'hooks/User/useUserInfo';
import { type MouseEventHandler, useCallback, useMemo } from 'react';
import useLegacyDashboard from 'state/contexts/LegacyDashboard/useLegacyDashboard';
import type { DropdownItemEntry, ManageSimItemHook } from '../types';

const useSetDataUsageLimit: ManageSimItemHook = ({
  context,
  selectedSims,
  disabled,
  onActionTaken,
}) => {
  // Single SIM case
  const simId = context === 'details' && selectedSims ? [...selectedSims.keys()][0] : undefined;
  const { simDetails } = useSimDetails(simId);
  const { deviceDetails } = useSimDeviceDetails(simId);
  const { claimedDeviceId } = deviceDetails ?? {};

  const { setDataUsageLimits } = useLegacyDashboard() ?? {};

  const onClick = useCallback<MouseEventHandler>(
    (e) => {
      if ((e.target as HTMLElement).tagName === 'A') {
        return;
      }

      if (!setDataUsageLimits) {
        throw new Error('Tried to set data usage limit outside of legacy dashboard context.');
      }

      if (claimedDeviceId) {
        setDataUsageLimits([claimedDeviceId]);
      } else {
        const deviceIds: Array<DeviceId | undefined> = [...(selectedSims?.values() ?? [])];
        setDataUsageLimits(deviceIds.filter<DeviceId>((id): id is DeviceId => !!id));
      }
      onActionTaken();
    },
    [claimedDeviceId, onActionTaken, setDataUsageLimits, selectedSims]
  );

  const { networkStatusGroups } = useSimStatuses();

  const { organizationRole } = useUserInfo();

  const show =
    ['admin', 'manager', 'editor'].includes(organizationRole) &&
    (networkStatusGroups?.deactivated?.findIndex(
      (state) => simDetails?.networkStatus?.localeCompare(state) === 0
    ) ?? -1) === -1;

  return useMemo<DropdownItemEntry | false>(
    () =>
      show && {
        id: `sim${context}-datausagelimits-updatelimit`,
        disabled,
        limit: 10,
        iconStart: 'Gauge',
        children: 'Set data usage limit...',
        onClick,
      },
    [context, disabled, onClick, show]
  );
};

export default useSetDataUsageLimit;
